import { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useSearchParams } from 'react-router-dom'
import { notamStatus, type Notam, type Position } from '@jupi/shared'
import { RemoteContent, Title, useServerClock, useSnackbar } from '@jupi/ui'
import { useAirspaceRings, useEfinNotams } from '../../api/notams'
import type { PlanLegInput } from '@jupi/shared'
import Button from '@mui/material/Button'
import { Icon } from '@iconify/react'
import { useCurrentPlan, useSavePlan, useWaypoints, type ReportingPoint } from '../../api/plan'
import type { MapNotams, NotamFocus, RouteDrop } from './FlightMap'
import { AIRSPACE_GROUPS, DEFAULT_AIRSPACE_VIEW, type AirspaceGroup, type AirspaceView } from './mapStyle/airspaceLayers'
import { DEFAULT_REPORTING_POINT_VIEW, type ReportingPointView } from './mapStyle/reportingPointLayers'
import AerodromeDialog from './AerodromeDialog'
import DataSourcesNote from './DataSourcesNote'
import { isUserPoint, legFromPosition, legLabel, legFromReportingPoint, legFromWaypoint, reverseLegs } from './planLegs'

// MapLibre is big; only this page pays for it.
const FlightMap = lazy(() => import('./FlightMap'))

// A per-viewer convenience: which airspace this pilot likes to see.
const VIEW_KEY = 'jupi.plan.map.airspace'

function loadView(): AirspaceView {
  try {
    const saved = JSON.parse(localStorage.getItem(VIEW_KEY) ?? 'null') as Partial<AirspaceView> | null
    if (!saved) return DEFAULT_AIRSPACE_VIEW
    return {
      groups: (saved.groups ?? []).filter((g): g is AirspaceGroup => AIRSPACE_GROUPS.includes(g)),
      belowFl95: saved.belowFl95 ?? DEFAULT_AIRSPACE_VIEW.belowFl95,
    }
  } catch {
    return DEFAULT_AIRSPACE_VIEW
  }
}

const POINTS_KEY = 'jupi.plan.map.reportingPoints'
const POINT_KINDS = ['vrp', 'ifr'] as const

function loadPointView(): ReportingPointView {
  try {
    const saved = JSON.parse(localStorage.getItem(POINTS_KEY) ?? 'null') as Partial<ReportingPointView> | null
    return { ...DEFAULT_REPORTING_POINT_VIEW, ...saved }
  } catch {
    return DEFAULT_REPORTING_POINT_VIEW
  }
}

const NOTAMS_KEY = 'jupi.plan.map.notams'

function loadNotamView(): boolean {
  try {
    return localStorage.getItem(NOTAMS_KEY) !== 'off'
  } catch {
    return true
  }
}

// What the chart shows: in force now or starting within a day. A firing
// area booked for next week would only clutter today's picture.
const NOTAM_HORIZON_MS = 24 * 60 * 60_000

function onChartNow(n: Notam, now: number): boolean {
  const status = notamStatus(n, now)
  return status === 'active' || (status === 'upcoming' && Date.parse(n.from) < now + NOTAM_HORIZON_MS)
}

function boundsOf(positions: Position[]): NotamFocus['bounds'] | null {
  if (positions.length === 0) return null
  const lons = positions.map((p) => p[0])
  const lats = positions.map((p) => p[1])
  return [
    [Math.min(...lons), Math.min(...lats)],
    [Math.max(...lons), Math.max(...lats)],
  ]
}

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

/** `FI 06 AUG 2026, EE 01 OCT 2026`: the edition of each country's airspace, from the manifest. */
function useAirspaceEditions(): string | null {
  const [editions, setEditions] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    fetch('/map/manifest.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((m: { countries?: Record<string, { airspace?: { cycle: string } }> } | null) => {
        if (!live || !m?.countries) return
        const parts = Object.entries(m.countries).flatMap(([country, c]) => {
          const [y, mo, d] = (c.airspace?.cycle ?? '').split('-')
          const month = MONTHS[Number(mo) - 1]
          return month ? [`${country.toUpperCase()} ${d} ${month} ${y}`] : []
        })
        setEditions(parts.length ? parts.join(', ') : null)
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [])
  return editions
}

// The waypoints the member can plan with, the current flight plan's route
// drawn over them, and Finnish, Estonian and Swedish airspace and reporting points from the eAIPs
// beneath both. A reporting point's popup appends it to the flight plan.
export default function MapPage() {
  const { t } = useTranslation()
  const [view, setView] = useState<AirspaceView>(loadView)
  const editions = useAirspaceEditions()
  const airspaceLabels = useMemo(
    () => ({ class: t('plan.map.airspace.class'), activity: t('plan.map.airspace.activity') }),
    [t],
  )
  const [pointView, setPointView] = useState<ReportingPointView>(loadPointView)
  const reportingPointLabels = useMemo(
    () => ({
      vrp: t('plan.map.points.vrp'),
      ifr: t('plan.map.points.ifr'),
      compulsory: t('plan.map.points.compulsory'),
      onRequest: t('plan.map.points.onRequest'),
      routes: t('plan.map.points.routes'),
      addToPlan: t('plan.map.points.addToPlan'),
    }),
    [t],
  )
  const [showNotams, setShowNotams] = useState(loadNotamView)
  const toggleNotams = () => {
    setShowNotams(!showNotams)
    try {
      localStorage.setItem(NOTAMS_KEY, showNotams ? 'off' : 'on')
    } catch {
      // As for the airspace choice.
    }
  }
  const [params] = useSearchParams()
  const focusId = params.get('notam')
  const notamLabels = useMemo(() => ({ activates: t('plan.map.notam.activates'), perm: t('notam.perm') }), [t])
  const togglePoints = (kind: keyof ReportingPointView) => {
    const next = { ...pointView, [kind]: !pointView[kind] }
    setPointView(next)
    try {
      localStorage.setItem(POINTS_KEY, JSON.stringify(next))
    } catch {
      // As for the airspace choice.
    }
  }
  const changeView = (next: AirspaceView) => {
    setView(next)
    try {
      localStorage.setItem(VIEW_KEY, JSON.stringify(next))
    } catch {
      // Private mode or blocked storage: the choice just is not remembered.
    }
  }
  const toggleGroup = (group: AirspaceGroup) =>
    changeView({
      ...view,
      groups: view.groups.includes(group) ? view.groups.filter((g) => g !== group) : [...view.groups, group],
    })
  const { data: allWaypoints = [], isLoading, error } = useWaypoints()
  // VRPs are waypoints too, but have their own layer and symbol.
  const waypoints = useMemo(() => allWaypoints.filter((w) => w.waypointType !== 'vrp'), [allWaypoints])
  const [aerodrome, setAerodrome] = useState<string | null>(null)
  const { data: current } = useCurrentPlan()
  const savePlan = useSavePlan()
  const { showSnackbar } = useSnackbar()
  const addToPlan = async (point: ReportingPoint) => {
    if (!current) return
    const { updatedAt: _updatedAt, legs, ...plan } = current.plan
    try {
      await savePlan.mutateAsync({
        ...plan,
        legs: [...legs.map(({ position: _position, ...leg }) => leg), legFromReportingPoint(point, waypoints)],
      })
      showSnackbar(t('plan.map.points.added', { code: point.code }))
    } catch {
      showSnackbar(t('plan.map.points.addFailed'), { severity: 'error' })
    }
  }
  // The plan's legs as the API takes them back, changed by `change`.
  const editLegs = async (change: (legs: PlanLegInput[]) => PlanLegInput[], failed: string) => {
    if (!current) return
    const { updatedAt: _updatedAt, legs, ...plan } = current.plan
    try {
      await savePlan.mutateAsync({ ...plan, legs: change(legs.map(({ position: _position, ...leg }) => leg)) })
    } catch {
      showSnackbar(failed, { severity: 'error' })
    }
  }
  const insertPoint = (before: number, drop: RouteDrop) =>
    editLegs((legs) => {
      const wind = { windDir: current?.plan.windDir ?? 0, windSpeedKt: current?.plan.windSpeedKt ?? 0 }
      const known = drop.kind === 'waypoint' ? allWaypoints.find((w) => w.code === drop.point.code) : undefined
      if (drop.kind === 'waypoint' && !known) return legs
      const leg =
        known
          ? legFromWaypoint(known)
          : drop.kind === 'reporting'
            ? legFromReportingPoint(drop.point, waypoints)
            : drop.kind === 'free'
              ? legFromPosition(drop.lat, drop.lon, waypoints, legs.map((l) => l.code))
              : legFromWaypoint(allWaypoints[0]!)
      return [...legs.slice(0, before), { ...leg, ...wind }, ...legs.slice(before)]
    }, t('plan.map.route.editFailed'))
  const removePoint = (index: number) =>
    editLegs((legs) => legs.filter((_, i) => i !== index), t('plan.map.route.editFailed'))
  const renamePoint = (index: number, code: string, name: string) =>
    editLegs((legs) => legs.map((l, i) => (i === index ? { ...l, code, name: name || null } : l)), t('plan.map.route.editFailed'))
  const reverse = () => editLegs(reverseLegs, t('plan.map.route.editFailed'))
  const { data: bulletin } = useEfinNotams({ enabled: showNotams || focusId !== null })
  const { utcMs } = useServerClock()
  const minute = Math.floor(utcMs / 60_000) * 60_000
  const mapNotams = useMemo((): MapNotams | null => {
    if (!bulletin || (!showNotams && !focusId)) return null
    const list = bulletin.notams.filter((n) => n.id === focusId || (showNotams && onChartNow(n, minute)))
    const aerodromes = new Map(waypoints.filter((w) => /^[A-Z]{4}$/.test(w.code)).map((w) => [w.code, w]))
    return { list, aerodromes }
  }, [bulletin, showNotams, focusId, minute, waypoints])
  const focused = bulletin?.notams.find((n) => n.id === focusId)
  const focusAirspaces = useAirspaceRings(focused?.airspaces ?? [])
  const focus = useMemo((): NotamFocus | null => {
    if (!focused) return null
    const at = focused.location ? waypoints.find((w) => w.code === focused.location) : undefined
    const bounds = boundsOf([
      ...focused.areas.map((a) => a.position),
      ...[...(focusAirspaces?.values() ?? [])].flat(2),
      ...(focused.areas.length === 0 && at ? [[at.lon, at.lat] as Position] : []),
    ])
    return bounds && { notamId: focused.id, bounds }
  }, [focused, focusAirspaces, waypoints])

  // Memoised: the map re-fits itself to the route whenever it changes.
  const route = useMemo(
    () =>
      (current?.plan.legs ?? []).flatMap((leg, index) =>
        leg.lat !== null && leg.lon !== null ? [{ ...legLabel(leg), lat: leg.lat, lon: leg.lon, index, editable: isUserPoint(leg) }] : [],
      ),
    [current],
  )

  const first = current?.plan.legs[0]
  const radius = current?.plan.localRadiusNm ?? null
  const localCircle = useMemo(
    () => (radius && first?.lat != null && first.lon != null ? { lat: first.lat, lon: first.lon, radiusNm: radius } : null),
    [radius, first?.lat, first?.lon],
  )

  const loading = (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
      <CircularProgress />
    </Box>
  )

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Title label={t('nav.planMap')} />
        <Typography variant="body2" color="text.secondary">
          {t('plan.map.waypoints', { count: waypoints.length })}
        </Typography>
      </Stack>
      {route.length >= 2 && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<Icon icon="mdi:swap-horizontal" />} onClick={() => void reverse()}>
            {t('plan.editor.reverse')}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t('plan.map.route.hint')}
          </Typography>
        </Stack>
      )}
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1, my: 1 }}>
        {AIRSPACE_GROUPS.map((group) => (
          <Chip
            key={group}
            size="small"
            label={t(`plan.map.layers.${group}`)}
            color={view.groups.includes(group) ? 'primary' : 'default'}
            variant={view.groups.includes(group) ? 'filled' : 'outlined'}
            onClick={() => toggleGroup(group)}
          />
        ))}
        <Chip
          size="small"
          label={t('plan.map.layers.belowFl95')}
          color={view.belowFl95 ? 'secondary' : 'default'}
          variant={view.belowFl95 ? 'filled' : 'outlined'}
          onClick={() => changeView({ ...view, belowFl95: !view.belowFl95 })}
        />
        <Chip
          size="small"
          label={t('plan.map.layers.notams')}
          color={showNotams ? 'warning' : 'default'}
          variant={showNotams ? 'filled' : 'outlined'}
          onClick={toggleNotams}
        />
        {POINT_KINDS.map((kind) => (
          <Chip
            key={kind}
            size="small"
            label={t(`plan.map.layers.${kind}`)}
            color={pointView[kind] ? 'primary' : 'default'}
            variant={pointView[kind] ? 'filled' : 'outlined'}
            onClick={() => togglePoints(kind)}
          />
        ))}
      </Stack>
      <RemoteContent isLoading={isLoading} error={error}>
        {/* The shell's top bar, area tabs and bottom bar take about 200 px
            on a phone, the top bar alone about 140 on a desktop; the map
            gets the rest, but never less than a usable 320. */}
        <Box sx={{ height: { xs: 'max(320px, calc(100dvh - 240px))', md: 'max(420px, calc(100dvh - 180px))' } }}>
          <Suspense fallback={loading}>
            <FlightMap
              points={waypoints}
              route={route}
              localCircle={localCircle}
              onInsertPoint={current ? (before, drop) => void insertPoint(before, drop) : undefined}
              onRemovePoint={current ? (index) => void removePoint(index) : undefined}
              onRenamePoint={current ? (index, code, name) => void renamePoint(index, code, name) : undefined}
              renameLabel={t('plan.editor.pointLabel')}
              descriptionLabel={t('plan.editor.pointDescription')}
              removeLabel={t('plan.map.route.remove')}
              moreInfoLabel={t('plan.map.moreInfo')}
              airspace={view}
              airspaceLabels={airspaceLabels}
              reportingPoints={pointView}
              reportingPointLabels={reportingPointLabels}
              onAddToPlan={current ? (point) => void addToPlan(point) : undefined}
              onOpenAerodrome={setAerodrome}
              detailsLabel={t('plan.aerodrome.open')}
              notams={mapNotams}
              notamLabels={notamLabels}
              focus={focus}
              sx={{ height: '100%' }}
            />
          </Suspense>
        </Box>
      </RemoteContent>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Map{' '}
        <a href="https://openfreemap.org" target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
          OpenFreeMap
        </a>{' '}
        ©{' '}
        <a href="https://www.openmaptiles.org/" target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
          OpenMapTiles
        </a>{' '}
        ©{' '}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
          OpenStreetMap contributors
        </a>{' '}
        · Airports ©{' '}
        <a href="https://ourairports.com" target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
          OurAirports
        </a>
        {editions && <> · {t('plan.map.airspace.edition', { edition: editions })}</>}
        {mapNotams && <> · NOTAM Fintraffic AIS</>}
        <DataSourcesNote prefix=" · " /> · {t('plan.dataSources.licence')} ·{' '}
        <strong>{t('plan.map.airspace.notOperational')}</strong>
      </Typography>
      <AerodromeDialog code={aerodrome} onClose={() => setAerodrome(null)} />
    </Box>
  )
}
